import React, { useState } from "react";
import {
  Container,
  Typography,
  Grid,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
} from "@mui/material";
import Layout from "../components/layout/CompliceLayout";
import { Bar, Pie } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PieController,
  Title,
  Tooltip,
  Legend,
} from "chart.js";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PieController,
  Title,
  Tooltip,
  Legend
);

const CompliceReport = () => {
  const [selectedPeriod, setSelectedPeriod] = useState("quarterly");

  // Mock compliance data (replace with actual data source)
  const complianceData = {
    quarterly: {
      barData: {
        labels: [
          "Tax Compliance",
          "Financial Reporting",
          "Regulatory Filings",
          "Internal Controls",
        ],
        datasets: [
          {
            label: "Compliance Score",
            data: [85, 92, 78, 88],
            backgroundColor: [
              "rgba(75, 192, 192, 0.6)",
              "rgba(54, 162, 235, 0.6)",
              "rgba(255, 206, 86, 0.6)",
              "rgba(153, 102, 255, 0.6)",
            ],
          },
        ],
      },
      pieData: {
        labels: ["Compliant", "Minor Violations", "Major Violations"],
        datasets: [
          {
            data: [75, 20, 5],
            backgroundColor: [
              "rgba(75, 192, 192, 0.6)",
              "rgba(255, 206, 86, 0.6)",
              "rgba(255, 99, 132, 0.6)",
            ],
          },
        ],
      },
    },
    annual: {
      barData: {
        labels: [
          "Tax Compliance",
          "Financial Reporting",
          "Regulatory Filings",
          "Internal Controls",
        ],
        datasets: [
          {
            label: "Compliance Score",
            data: [88, 95, 82, 90],
            backgroundColor: [
              "rgba(75, 192, 192, 0.6)",
              "rgba(54, 162, 235, 0.6)",
              "rgba(255, 206, 86, 0.6)",
              "rgba(153, 102, 255, 0.6)",
            ],
          },
        ],
      },
      pieData: {
        labels: ["Compliant", "Minor Violations", "Major Violations"],
        datasets: [
          {
            data: [80, 15, 5],
            backgroundColor: [
              "rgba(75, 192, 192, 0.6)",
              "rgba(255, 206, 86, 0.6)",
              "rgba(255, 99, 132, 0.6)",
            ],
          },
        ],
      },
    },
    monthly: {
      barData: {
        labels: [
          "Tax Compliance",
          "Financial Reporting",
          "Regulatory Filings",
          "Internal Controls",
        ],
        datasets: [
          {
            label: "Compliance Score",
            data: [82, 90, 75, 86],
            backgroundColor: [
              "rgba(75, 192, 192, 0.6)",
              "rgba(54, 162, 235, 0.6)",
              "rgba(255, 206, 86, 0.6)",
              "rgba(153, 102, 255, 0.6)",
            ],
          },
        ],
      },
      pieData: {
        labels: ["Compliant", "Minor Violations", "Major Violations"],
        datasets: [
          {
            data: [70, 25, 5],
            backgroundColor: [
              "rgba(75, 192, 192, 0.6)",
              "rgba(255, 206, 86, 0.6)",
              "rgba(255, 99, 132, 0.6)",
            ],
          },
        ],
      },
    },
  };

  const barOptions = {
    responsive: true,
    plugins: {
      legend: { position: "top" },
      title: {
        display: true,
        text: "Compliance Scores by Category",
      },
    },
    scales: {
      y: {
        beginAtZero: true,
        max: 100,
        title: {
          display: true,
          text: 'Compliance Score (%)'
        }
      }
    }
  };

  const pieOptions = {
    responsive: true,
    plugins: {
      legend: { position: "right" },
      title: {
        display: true,
        text: "Overall Compliance Status",
      },
    },
  };

  const handlePeriodChange = (event) => {
    setSelectedPeriod(event.target.value);
  };

  return (
    <Layout>
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Compliance Report Dashboard
        </Typography>

        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel>Reporting Period</InputLabel>
            <Select
              value={selectedPeriod}
              label="Reporting Period"
              onChange={handlePeriodChange}
            >
              <MenuItem value="quarterly">Quarterly</MenuItem>
              <MenuItem value="annual">Annual</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={7}>
            <Paper sx={{ p: 2, height: "100%" }}>
              <Bar
                data={complianceData[selectedPeriod].barData}
                options={barOptions}
              />
            </Paper>
          </Grid>
          <Grid item xs={12} md={5}>
            <Paper sx={{ p: 2, height: "100%" }}>
              <Pie
                data={complianceData[selectedPeriod].pieData}
                options={pieOptions}
              />
            </Paper>
          </Grid>

          {/* Additional Compliance Details */}
          <Grid item xs={12}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6">Compliance Highlights</Typography>
              <Typography variant="body1">
                • Total Compliance Score: {
                  complianceData[selectedPeriod].barData.datasets[0].data.reduce((a, b) => a + b, 0) / 
                  complianceData[selectedPeriod].barData.datasets[0].data.length
                }% 
                • Areas Requiring Attention: Financial Reporting Processes 
                • Recent Regulatory Updates: Reviewed and Incorporated
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Container>
    </Layout>
  );
};

export default CompliceReport;